// /en/translate/naati/online/$country — NAATI Online country page (EN)
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { NAATI_COUNTRIES, getNaatiCountry } from "@/data/naati-countries";
import { naatiCountrySectionsEn } from "@/data/naati-online-content";
import { PoliceSections } from "@/components/PoliceSections";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { ContactCTA, FaqAccordion } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import NaatiTripleKeyLinks from "@/components/NaatiTripleKeyLinks";
import NaatiAuthorByline from "@/components/NaatiAuthorByline";
import NaatiDoctypeMatrix from "@/components/NaatiDoctypeMatrix";
import { naatiHowToSchema, naatiSpeakableSchema, naatiOfferCatalog } from "@/lib/naati-schema";
import { Plane, MessageCircle, Phone, BadgeCheck } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/translate/naati/online/$country")({
  loader: ({ params }) => {
    if (!getNaatiCountry(params.country)) throw notFound();
    return { slug: params.country };
  },
  head: ({ params }) => {
    const c = getNaatiCountry(params.country);
    if (!c) return { meta: [{ title: ctaTitle("Not found", "/en/translate/naati/online/$country", "en")}] };
    const url = `${SITE}/en/translate/naati/online/${c.slug}`;
    const title = `NAATI Translation to ${c.nameEn} — DHL Express ${c.shippingDays} | NYC`;
    const desc = `NAATI-certified translation shipped to ${c.nameEn}. Accepted for ${c.visaTypes.slice(0, 3).join(", ")} + DHL. Talk to a specialist today.`;
    return {
      meta: [
        { title: ctaTitle(title, "/en/translate/naati/online/$country", "en") }, { name: "description", content: ctaDesc(desc, "/en/translate/naati/online/$country", "en")},
        { property: "og:title", content: ctaTitle(title, "/en/translate/naati/online/$country", "en")}, { property: "og:description", content: ctaDesc(desc, "/en/translate/naati/online/$country", "en")},
        { property: "og:url", content: url }, { property: "og:locale", content: "en_US" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/translate/naati/online/${c.slug}`, `/en/translate/naati/online/${c.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema(),
          naatiSpeakableSchema(url),
          naatiOfferCatalog({ url, inLanguage: "en" }),
          naatiHowToSchema({
            url,
            name: `How to order NAATI translation shipped to ${c.nameEn}`,
            inLanguage: "en",
            estimatedCostThb: 800 + c.shippingFeeThb,
            steps: [
              { name: "Send files", text: `Send scans of your documents (${c.popularDocs.slice(0,2).join(", ")}) via LINE @nycli or email.` },
              { name: "Confirm quote", text: `Receive a quote from THB 800/page + DHL THB ${c.shippingFeeThb.toLocaleString()} to ${c.capital}.` },
              { name: "Translate & certify", text: `NAATI Professional translator translates and applies NAATI stamp + CPN within 2-3 business days.` },
              { name: "DHL shipment", text: `DHL Express to ${c.capital} in ${c.shippingDays} with tracking number.` },
            ],
          }),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: `NAATI Translation to ${c.nameEn}`,
              serviceType: "NAATI Certified Translation",
              areaServed: { "@type": "Country", name: c.nameEn },
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Language Institute", telephone: "+66832494999", url: SITE },
              offers: { "@type": "Offer", price: 800, priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
        ]),
      ],
    };
  },
  component: NaatiOnlineCountryEnPage,
});

function NaatiOnlineCountryEnPage() {
  const { slug } = Route.useLoaderData() as { slug: string };
  const c = getNaatiCountry(slug)!;
  const sections = naatiCountrySectionsEn(c);
  const related = NAATI_COUNTRIES.filter((x) => x.region === c.region && x.slug !== c.slug).slice(0, 6);
  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_55%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/en" className="hover:text-gold">Home</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/en/translate/naati/online" className="hover:text-gold">NAATI Online</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{c.nameEn}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Plane className="h-3.5 w-3.5" /> NAATI to {c.nameEn}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            NAATI Certified Translation to {c.nameEn}
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            DHL Express to {c.capital} in {c.shippingDays} · Shipping from THB {c.shippingFeeThb.toLocaleString()} · NAATI Professional Translators · From THB 1,300/page
          </p>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md">
              <MessageCircle className="h-4 w-4" /> LINE @nycli
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md">
              <Phone className="h-4 w-4" /> +66 83-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote data-speakable className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable
          </span>
          NAATI Certified Translation to {c.nameEn} by NAATI Professional Translators · DHL Express to {c.capital} in {c.shippingDays} · From THB 800/page · {c.notes}
        </blockquote>

        <NaatiAuthorByline lang="en" />

        <div className="mt-10"><PoliceSections sections={sections} /></div>

        <section className="mt-12"><ContactCTA /></section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">FAQ</h2>
          <div className="mt-5">
            <FaqAccordion
              items={[
                { q: `Is NAATI accepted in ${c.nameEn}?`, a: `${c.notes} Recognized by ${c.authorities.join(", ")}.` },
                { q: `Which ${c.nameEn} visa categories require NAATI?`, a: c.visaTypes.join(", ") },
                { q: `How long does DHL take to ${c.nameEn}?`, a: `${c.shippingDays}. Shipping from THB ${c.shippingFeeThb.toLocaleString()}.` },
                { q: `What documents are commonly translated for ${c.nameEn}?`, a: c.popularDocs.join(" · ") },
                { q: `What is the NAATI translation rate?`, a: `From THB 1,300/page for personal documents · THB 1,200-1,500/page for academic documents · NAATI stamp included.` },
              ]}
            />
          </div>
        </section>

        {related.length > 0 && (
          <section className="mt-14 border-t border-slate-200 pt-10">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Nearby Countries in {c.region}</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((r) => (
                <Link key={r.slug} to="/en/translate/naati/online/$country" params={{ country: r.slug }} className="rounded-2xl border border-slate-200 bg-white p-4 hover:border-gold/50">
                  <div className="font-display text-sm font-extrabold text-navy-deep">{r.nameEn}</div>
                  <div className="mt-0.5 text-[11px] text-slate-500">{r.shippingDays} · THB {r.shippingFeeThb.toLocaleString()}</div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Phase P-EN-NAATI-ACT-DESTINATION — forward edges into combined notary+NAATI matrix */}
        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            Need a notarial act WITH your NAATI translation for {c.nameEn}?
          </h2>
          <p className="mt-2 text-sm text-slate-600">
            Affidavits, statutory declarations and jurats — notarised + NAATI-translated in one workflow.
          </p>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { slug: "affidavit", name: "Affidavit" },
              { slug: "statutory-declaration", name: "Statutory Declaration" },
              { slug: "jurat", name: "Jurat / Sworn Statement" },
              { slug: "affidavit-single", name: "Single-Status Affidavit" },
              { slug: "affidavit-support", name: "Affidavit of Support" },
              { slug: "certify-translation", name: "Certified Translation" },
            ].map((x) => (
              <Link
                key={x.slug}
                to="/en/translate/naati/$destination/$act"
                params={{ destination: c.slug, act: x.slug }}
                className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
                data-track-zone="naati-country-to-act"
              >
                <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-700">
                  {x.name} + NAATI for {c.nameEn}
                </div>
                <div className="mt-1 text-[11px] text-slate-500">Notarised + translated · DHL {c.shippingDays}</div>
              </Link>
            ))}
          </div>
        </section>

        <NaatiDoctypeMatrix lang="en" />

        <NaatiTripleKeyLinks mode="en" countryNameTh={c.nameTh} countryNameEn={c.nameEn} />
      </div>

      <TrustLayer variant="compact" />
    </main>
  );
}
